import React, { useEffect, useState, useContext } from 'react'
import { PlayerContext, SeasonContext, TeamContext } from '../../Context'
import { PLL_LINKS } from '../../Config/config'
import { AutocompleteContainer } from '../../Components/Search'
import { TeamLeadersContainer } from '../../Components/TeamLeaders/TeamLeadersContainer'
import { TeamStatsTable } from '../../Components/TeamStats/TeamStatsTable'
import { PageHeader, SectionHeading } from '../../Components/Headers'
import { TeamTicker } from '../../Components/GameTicker'
import { FeedCardList } from '../../Components/Feed/FeedCardList'
import { TeamTopLinkBar, TeamMobileLinkBar } from '../../Components/TeamLinkBar'
import { SloganCard } from '../../Components/ContentCard'
import { ShopCard } from '../../Components/Shop/ShopCard'
import {
  Box,
  BoxWithColumns,
  SidePanelLayout,
  MainColumn,
  SidePanel,
  MobileMenuContainer,
  HSpace,
  PageContainerMain,
} from '../../Components/Layout'
import { createTeamDynamicLink } from '../../Utils'
import { createShopUTM } from '../../Routing'
import { GameResults } from './components/results/GameResults'
import { CardLoading } from '../../Components/Loading'
import { TeamInjury } from './components/Injuries'
import { GeneralAppCallout } from '../../Components/Layout/Overlays/AppCallouts/GeneralAppCallout'
import { Banner, TeamGQL } from '../../Api'
import { determineSeasonStatsStatus } from '../../Utils'
import {
  CURRENT_YEAR,
  STANDINGS_YEAR,
  STATS_YEAR,
  isChampSeriesLive,
} from '../../Config/League/league.config'
import { AdUnit } from '../../Components/Ads/AdUnit'
import { ShopList } from '../../Components/Shop/ShopList/ShopList'
import { isChampSeriesTeam } from '../../Utils'
import { FeedCard } from '../../Components/Feed/FeedCard'
import { StandingsSection } from '../../Components/Standings/StandingsSection'
import { Error } from '../Errors'
import { champSeriesTeams } from '../../Constants/team'
import { SEASON_SELECTOR_TYPES } from '../../Constants/season'

const Team: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { seasonSelect } = useContext(SeasonContext)!
  const {
    team,
    banners,
    events,
    stats,
    csStats,
    standings,
    isTeamLoading,
    artLoading,
    teamFeed,
    teamMerchCollection,
    urlFullStats,
  } = useContext(TeamContext)!
  const { isRosterLoading, roster, champSeriesRoster } = useContext(PlayerContext)!
  const [banner, setBanner] = useState<Banner | null>(null)
  const [isCSTeam, setIsCSTeam] = useState<boolean>(false)

  useEffect(() => {
    if (!team) return
    if (banners.length > 0) {
      //Set the most recent banner
      setBanner(banners[0])
    }
    checkCSTeam(CURRENT_YEAR, team)
  }, [team, banners])

  const checkCSTeam = (year: number, tm: TeamGQL) => {
    let csTeams = champSeriesTeams[year]
    let isCS = !!isChampSeriesTeam(tm, csTeams)
    setIsCSTeam(isCS)
  }

  if (isTeamLoading)
    return (
      <PageContainerMain useFooter>
        <AdUnit bannerType="bannerMain" aria-label="Team page ad loading" />
        <Box>Loading...</Box>
        <AdUnit bannerType="bannerSecond" aria-label="Team page ad loading second" />
        <AdUnit bannerType="bannerThird" aria-label="Team page ad loading third" />
      </PageContainerMain>
    )

  if (!isTeamLoading && !team) {
    return <Error errorNo={404} />
  }
  if (!team) return

  return (
    <>
      <PageContainerMain useFooter>
        <GeneralAppCallout
          text={`Support the ${team?.fullName} in the official PLL App`}
          appLink={createTeamDynamicLink(team!)}
          location={`${team?.fullName}_Page`}
        />
        <PageHeader
          banner={banner}
          img={banner ? banner.topUrl : team.teamBanner}
          link={banner?.topLink ? banner.topLink : null}
          alt={`${team.fullName} banner`}
        />
        <AutocompleteContainer />
        <AdUnit bannerType="bannerMain" aria-label="Team page ad main" />
        <Box>
          <TeamTopLinkBar roster stats hideOnMobile />
          <HSpace hideOnMobile />
          {artLoading ? (
            <>
              <CardLoading size="lg" />
              <CardLoading size="lg" />
            </>
          ) : (
            teamFeed?.team &&
            teamFeed.team.length > 0 && (
              <FeedCard feedItem={teamFeed.team[0]} size="featured" />
            )
          )}
        </Box>

        {events && events.length > 0 && (
          <Box padding={4}>
            <SectionHeading linkText={'See full schedule'} linkUrl={PLL_LINKS.schedule}>
              <strong>Game schedule</strong>
            </SectionHeading>
            <TeamTicker teamId={team.officialId} />
          </Box>
        )}

        {teamFeed?.team && teamFeed.team.length > 0 && (
          <Box>
            <SectionHeading>
              <strong>{team.fullName} latest</strong>
            </SectionHeading>
            <FeedCardList
              feedItems={teamFeed?.team.slice(1)}
              section={'team_box_news'}
            />
          </Box>
        )}

        {teamMerchCollection && teamMerchCollection.length > 0 && (
          <Box>
            <ShopList
              products={teamMerchCollection}
              medium={`${team.fullName}_Page`}
              campaign={`${team.fullName}_page_collection`}
              listTitle="Trending in the Shop"
            />
          </Box>
        )}

        <SidePanelLayout panelOn="right">
          <MainColumn>
            {!isRosterLoading && (roster.length > 0 || champSeriesRoster.length > 0) && (
              <Box>
                <SectionHeading linkText={'See full stats'} linkUrl={urlFullStats()}>
                  <strong>
                    {isChampSeriesLive && isCSTeam ? (
                      <>{STATS_YEAR} Championship Series leaders</>
                    ) : (
                      <>{STATS_YEAR} Season leaders</>
                    )}
                  </strong>
                </SectionHeading>
                {determineSeasonStatsStatus(events) !== 'Pending' ? (
                  <TeamLeadersContainer />
                ) : (
                  <p>No stats for this season segment.</p>
                )}
              </Box>
            )}

            <Box>
              <SectionHeading
                linkText={'See full stats'}
                linkUrl={urlFullStats()}
                lowMargin
              >
                <strong>
                  {STATS_YEAR}{' '}
                  {isChampSeriesLive && isCSTeam
                    ? 'Chamionship Series stats'
                    : `Season stats`}
                </strong>
              </SectionHeading>
              {isChampSeriesLive &&
              determineSeasonStatsStatus(events) !== 'Pending' &&
              csStats ? (
                <TeamStatsTable stats={csStats} />
              ) : !isChampSeriesLive &&
                determineSeasonStatsStatus(events) !== 'Pending' &&
                stats ? (
                <TeamStatsTable stats={stats} />
              ) : (
                <p>The {team.fullName} have no stats yet.</p>
              )}
            </Box>
          </MainColumn>

          <SidePanel>
            <BoxWithColumns layout="aa" padding={4}>
              {standings.length > 0 && (
                <div>
                  <SectionHeading lowMargin>
                    <strong>
                      {STANDINGS_YEAR}{' '}
                      {isChampSeriesLive
                        ? 'Champ Series Standings'
                        : 'Reg. Season Standings'}
                    </strong>
                  </SectionHeading>
                  <StandingsSection
                    items={standings}
                    seasonSelect={seasonSelect[SEASON_SELECTOR_TYPES.STANDINGS]}
                    section={`${team.fullName}_page`}
                  />
                </div>
              )}
              <GameResults />
              <div>
                <SectionHeading headingText="Gear Up" lowMargin />
                <ShopCard
                  linkUrl={createShopUTM({
                    medium: `${team.fullName}_Page`,
                    slug: `collections/${team.fullName}`,
                    campaign: 'team_shop_button',
                  })}
                  productImg={team.shopImg}
                  team={team.fullName}
                />
              </div>
              <div className="wide">
                <SloganCard slogan={team.slogan} />
              </div>
            </BoxWithColumns>
          </SidePanel>
        </SidePanelLayout>
        <AdUnit bannerType="bannerSecond" aria-label="Team page ad second" />
        {!isRosterLoading && (
          <Box>
            <SectionHeading lowMargin>
              <strong>Team injuries</strong>
            </SectionHeading>
            <TeamInjury />
          </Box>
        )}
        <AdUnit bannerType="bannerThird" aria-label="Team page ad third" />
      </PageContainerMain>

      <MobileMenuContainer>
        <TeamMobileLinkBar roster stats />
      </MobileMenuContainer>
    </>
  )
}

export default Team
